import React, { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Hospital, Stethoscope, Users, ShieldCheck, BadgeCheck, Lock,
  Brain, Workflow, Banknote, FileSignature, Activity, MessageSquare,
  Sparkles, Download, ArrowLeft, Heart, Clock,
} from 'lucide-react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Seo } from '../components/seo/Seo';
import { EMAIL, NAP } from '../lib/siteConfig';

/**
 * PlatformOnePager — print-optimized "Platform Features" summary the
 * user can save as a PDF via the browser's native print-to-PDF dialog.
 *
 * Why a print-route instead of a server-rendered PDF?
 *   - Zero new dependencies (no puppeteer / weasyprint / pdfkit).
 *   - Pure additive frontend; respects RULE ZERO.
 *   - Works identically on every modern browser and OS — the user
 *     gets the standard "Save as PDF" destination from their print
 *     dialog, with consistent typography and brand fidelity.
 *
 * Behavior:
 *   - Route: `/platform-onepager` (public, no auth required).
 *   - If `?print=1` is present, the page auto-triggers window.print()
 *     after a short delay so the user lands directly in the save
 *     dialog. Otherwise they see a clean preview with a "Download PDF"
 *     button that triggers the same flow.
 *   - Print CSS hides the chrome (back link, download button) so only
 *     the content prints. @page sets letter size + 0.5in margins.
 *
 * Linked from: LandingPage FinalCTA, FeeSchedules FinalCTA, Footer
 * "Company" column. Each link points at `?print=1` so the file saves
 * immediately on click.
 */
export default function PlatformOnePager() {
  const [params] = useSearchParams();
  const autoPrint = params.get('print') === '1';

  useEffect(() => {
    if (!autoPrint) return;
    const t = setTimeout(() => {
      try { window.print(); } catch (_) { /* no-op */ }
    }, 700);
    return () => clearTimeout(t);
  }, [autoPrint]);

  const handlePrint = () => {
    try { window.print(); } catch (_) { /* no-op */ }
  };

  return (
    <div className="min-h-screen bg-white text-cn-text" data-testid="platform-onepager">
      <Seo
        title="CaraLoom — Platform Features (one-pager)"
        description="A single-page summary of the CaraLoom platform: referral & discharge orchestration, provider OS, credentialing automation, revenue cycle, family suite, compliance, and Ask Cara."
        path="/platform-onepager"
        noindex
      />

      {/* Print-only stylesheet — hides chrome + sets page geometry. */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.5in; }
          html, body { background: #ffffff !important; }
          .op-chrome { display: none !important; }
          .op-page  { box-shadow: none !important; padding: 0 !important; }
          .op-sheet { box-shadow: none !important; border: none !important; }
          a { text-decoration: none !important; color: inherit !important; }
          .op-no-break { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      {/* Screen-only chrome — hidden in print. */}
      <div className="op-chrome border-b border-cn-border bg-cn-bg/80 backdrop-blur sticky top-0 z-30">
        <div className="cn-container flex items-center justify-between h-16">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-cn-text-muted hover:text-cn-text" data-testid="onepager-back">
            <ArrowLeft className="w-4 h-4" /> Back to caraloom.com
          </Link>
          <button
            type="button"
            onClick={handlePrint}
            className="cn-btn cn-btn-primary text-sm font-bold"
            data-testid="onepager-download-pdf-btn"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="op-page py-10 md:py-14">
        <div className="op-sheet max-w-[820px] mx-auto bg-white border border-cn-border rounded-2xl shadow-sm px-8 md:px-12 py-10 md:py-12">
          {/* Header */}
          <header className="flex items-start justify-between gap-6 pb-6 border-b border-cn-border op-no-break">
            <div>
              <CaraLoomLogo size={36} />
              <p className="mt-3 text-sm text-cn-text-muted leading-relaxed max-w-md">
                Clinical-grade care, brought home with certainty. CaraLoom turns the discharge
                handoff into one continuous thread — from referral to accepted placement to first visit,
                watched for 30 days.
              </p>
            </div>
            <div className="text-right text-[11px] font-mono uppercase tracking-[0.14em] text-cn-text-muted">
              <div>Platform features</div>
              <div className="mt-1">One-pager · Feb 2026</div>
              <div className="mt-1">{NAP.addressLocality}, {NAP.addressRegion}</div>
            </div>
          </header>

          {/* Headline value */}
          <section className="mt-7 op-no-break">
            <div className="cn-eyebrow mb-2">What CaraLoom is</div>
            <h1 className="cn-display text-2xl md:text-3xl font-bold leading-tight tracking-tight">
              The post-acute operating system — for hospitals, providers, and the families they care for.
            </h1>
            <p className="mt-3 text-sm text-cn-text-muted leading-relaxed">
              A referral goes in. An accepted placement comes back — with a committed first-visit
              window and a clinician already on the schedule. Credentialing, F2F, 485/POC, NOA, claims,
              payouts, family status — all woven into one auditable record.
            </p>
          </section>

          {/* Three audiences */}
          <section className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 op-no-break">
            <AudienceCard
              icon={Hospital}
              label="Hospitals"
              title="Guarantee the placement."
              bullets={[
                'FHIR-native referral ingest + one-click planner intake',
                'AI prioritization + matching across credentialed providers',
                'Acceptance in hours with a committed first-visit window',
                '30-day readmission window watched, not hoped over',
              ]}
            />
            <AudienceCard
              icon={Stethoscope}
              label="Providers & clinicians"
              title="Run the whole practice on one thread."
              bullets={[
                'Pre-screened referrals routed from hospital partners',
                'Credentialing, F2F, 485/POC and NOA orchestrated for you',
                'Schedule, clinical notes, voice notes, telehealth',
                'Transparent, fast payouts on every accepted placement',
              ]}
            />
            <AudienceCard
              icon={Users}
              label="Families & caregivers"
              title="See who's coming, and when."
              bullets={[
                'Calm, read-only window into the discharge plan',
                'Verified clinicians + first-visit window shared with the family',
                'Care-circle invites + explicit, patient-authorized consent',
                'Private-pay marketplace where it is live',
              ]}
            />
          </section>

          {/* Feature catalog */}
          <section className="mt-9">
            <div className="cn-eyebrow mb-3">Feature catalog</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FeatureBlock
                icon={Workflow}
                title="Referral & discharge orchestration"
                items={[
                  'FHIR-native ingest + planner one-click entry',
                  'AI prioritization (acuity + capacity)',
                  'Provider acceptance with committed first-visit window',
                  'Status tracking + secure messaging across the thread',
                  '30-day readmission watch (NEWS2 + LACE)',
                ]}
              />
              <FeatureBlock
                icon={Activity}
                title="Provider OS"
                items={[
                  'Placement acceptance + provider work items',
                  'Visit fulfillment + schedule + clinical notes',
                  'Voice notes + AI SOAP drafting',
                  'Risk monitor with NEWS2 / LACE banding',
                  'Hospital scoreboard: latency, time-to-visit, readmits',
                ]}
              />
              <FeatureBlock
                icon={FileSignature}
                title="Credentialing automation"
                items={[
                  'NPPES primary-source verification (auto-run on intake)',
                  'OIG-LEIE exclusion screening on a daily cron',
                  'License monitoring with lapsed-license matcher block',
                  'F2F, 485/POC and NOA paperwork lift',
                  'Background check + CAQH ProView wiring',
                ]}
              />
              <FeatureBlock
                icon={Banknote}
                title="Revenue cycle"
                items={[
                  'Claims (837P EDI) + AI pre-submission audit',
                  'Missed-revenue inbox for unbilled clinical notes',
                  'Eligibility verification + denial management',
                  'AR aging + remittance posting',
                  'Patient billing + secure card checkout',
                ]}
              />
              <FeatureBlock
                icon={Heart}
                title="Family suite"
                items={[
                  'Family Status View (read-only, PHI-scoped)',
                  'Family marketplace (private-pay, market-gated)',
                  'Weekly "What changed" digest',
                  'Care circle invites + consent gating',
                  'Find Clinicians: AI matching with safety + clinical fit',
                ]}
              />
              <FeatureBlock
                icon={Sparkles}
                title="Ask Cara (AI assistant)"
                items={[
                  'BAA-locked LLM gateway (Vertex)',
                  'Platform-wide assistant with persistent sessions',
                  'Proactive nudges on intake, acceptance, and 30-day watch',
                  'Drawer UI accessible from every authenticated surface',
                ]}
              />
              <FeatureBlock
                icon={MessageSquare}
                title="Communications"
                items={[
                  'Secure in-app messaging + audit log',
                  'SMS via Twilio (telehealth links, RCM alerts)',
                  'Email via Resend',
                  'Telehealth video via Daily.co (private rooms + tokens)',
                ]}
              />
              <FeatureBlock
                icon={ShieldCheck}
                title="Compliance & trust"
                items={[
                  'PHI on covered paths only — BAAs for every subprocessor',
                  'Immutable audit trail + permission-scoped reads',
                  'MFA (TOTP) + login rate limiting + HttpOnly refresh',
                  'OIG-LEIE + license + background check coverage',
                  'HIPAA-aligned workflows end to end',
                ]}
              />
            </div>
          </section>

          {/* Scoreboard */}
          <section className="mt-9 op-no-break">
            <div className="cn-eyebrow mb-3">What goes on your scoreboard</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ScoreCard label="Referral → acceptance" value="Hours" body="Time from referral to a confirmed, accepted placement." />
              <ScoreCard label="Time to first visit" value="Committed" body="A scheduled window, shared across the thread." />
              <ScoreCard label="30-day readmissions" value="Watched" body="Risk-timed visits across the window that drives penalties." />
            </div>
          </section>

          {/* Trust pillars */}
          <section className="mt-9 op-no-break">
            <div className="cn-eyebrow mb-3">Built for the security review</div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <TrustPill icon={ShieldCheck} title="PHI on covered paths only"
                body="No patient data touches a model or service without a signed BAA behind it." />
              <TrustPill icon={Lock} title="Authorized, audited access"
                body="Every view of a patient record is permission-scoped and written to an immutable log." />
              <TrustPill icon={BadgeCheck} title="Verified clinicians"
                body="License, exclusion, and credentialing checks before anyone is matched to a patient." />
            </ul>
          </section>

          {/* Footer block */}
          <footer className="mt-10 pt-6 border-t border-cn-border flex flex-wrap items-end justify-between gap-4 op-no-break">
            <div className="text-xs text-cn-text-muted leading-relaxed max-w-md">
              <div className="flex items-center gap-1.5 mb-1.5 text-cn-text">
                <Brain className="w-3.5 h-3.5" />
                <span className="font-semibold">Where to next</span>
              </div>
              Book a hospital pilot, or sign in as a provider, clinician, or family member at{' '}
              <span className="text-cn-text font-medium">caraloom.com</span>.
              For partnerships and procurement, reach{' '}
              <a href={`mailto:${EMAIL}`} className="text-cn-primary font-semibold">{EMAIL}</a>.
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-cn-text-muted text-right">
              <div className="flex items-center gap-1.5 justify-end"><Clock className="w-3 h-3" /> Generated {new Date().toLocaleDateString()}</div>
              <div className="mt-1">© {new Date().getFullYear()} CaraLoom, Inc.</div>
              <div className="mt-1">caraloom.com</div>
            </div>
          </footer>
        </div>

        {/* Secondary screen-only CTA below the sheet */}
        <div className="op-chrome max-w-[820px] mx-auto mt-6 text-center">
          <button
            type="button"
            onClick={handlePrint}
            className="cn-btn cn-btn-primary text-sm font-bold"
            data-testid="onepager-download-pdf-btn-bottom"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <p className="mt-3 text-xs text-cn-text-muted">
            Your browser will open its print dialog — choose{' '}
            <span className="font-semibold text-cn-text">Save as PDF</span> as the destination.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Internal building blocks (kept inline to keep the file self-contained) ── */

function AudienceCard({ icon: Icon, label, title, bullets }) {
  return (
    <article
      className="rounded-xl border border-cn-border p-5 bg-cn-bone-100/40 op-no-break"
      data-testid={`onepager-audience-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-cn-primary">
        <Icon className="w-4 h-4" strokeWidth={1.8} /> {label}
      </div>
      <h3 className="cn-display text-base font-bold text-cn-text mt-2 leading-snug">{title}</h3>
      <ul className="mt-3 space-y-1.5 text-[12.5px] text-cn-text leading-snug">
        {bullets.map((b, i) => (
          <li key={i} className="pl-3.5 relative">
            <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full bg-cn-primary" aria-hidden="true" />
            {b}
          </li>
        ))}
      </ul>
    </article>
  );
}

function FeatureBlock({ icon: Icon, title, items }) {
  return (
    <div
      className="op-no-break"
      data-testid={`onepager-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-cn-primary/10 text-cn-primary">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </span>
        <h3 className="cn-display text-sm font-bold text-cn-text">{title}</h3>
      </div>
      <ul className="space-y-1.5 text-[12.5px] text-cn-text leading-snug">
        {items.map((it, i) => (
          <li key={i} className="pl-3.5 relative">
            <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full border border-cn-primary" aria-hidden="true" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScoreCard({ label, value, body }) {
  return (
    <div className="rounded-xl border border-cn-border bg-cn-bone-100/60 p-4 op-no-break">
      <div className="font-mono text-[0.65rem] uppercase tracking-[0.14em] text-cn-text-muted">{label}</div>
      <div className="cn-display text-2xl font-bold text-cn-primary mt-1.5">{value}</div>
      <p className="text-[12px] text-cn-text-muted leading-snug mt-1.5">{body}</p>
    </div>
  );
}

function TrustPill({ icon: Icon, title, body }) {
  return (
    <li className="rounded-xl border border-cn-border p-4 op-no-break">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-cn-primary/10 text-cn-primary">
          <Icon className="w-4 h-4" strokeWidth={1.8} />
        </span>
        <h4 className="cn-display text-sm font-semibold text-cn-text">{title}</h4>
      </div>
      <p className="mt-2 text-[12px] text-cn-text-muted leading-snug">{body}</p>
    </li>
  );
}
